import { downloadText } from '../utils/download';
import React, { useState, useEffect } from 'react';
import LoadErrorBox from './ui/LoadErrorBox';
import { useMoney } from '../hooks/useMoney';
import '../styles/components/SettlementReconciliation.css';
import Icon from './Icon';
import EmptyState from './EmptyState';
import { MetricCard } from './MetricCards';
import { Contributor, Settlement } from '../types/ui/Settlement';
import { useSettings } from '../contexts/SettingsContext';
import {
  generateSettlementMatrix,
  calculateWhoOwesWhom,
  createSettlement,
  updateSettlement,
  generateSettlementReport,
  exportSettlementAsCSV,
} from '../services/SettlementService';
import { loadContributors } from '../services/ContributorService';
import SearchableSelect from './ui/SearchableSelect';
import { MaskedValue } from './ui/MaskedValue';
import { useNotifications } from './NotificationProvider';

const SettlementReconciliation: React.FC = () => {
  const { showError } = useNotifications();
  const { formatCurrency } = useSettings();
  const money = useMoney();
  const [matrix, setMatrix] = useState<any>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [owings, setOwings] = useState<any[]>([]);
  const [selectedSettlement, setSelectedSettlement] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'bank-transfer' | 'card' | 'mobile-money'>('bank-transfer');
  const [paymentRef, setPaymentRef] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const onReport = () => { handleExportReport(); };
    const onCsv = () => { handleExportCSV(); };
    window.addEventListener('settlement-export-report', onReport);
    window.addEventListener('settlement-export-csv', onCsv);
    return () => {
      window.removeEventListener('settlement-export-report', onReport);
      window.removeEventListener('settlement-export-csv', onCsv);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshData = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const matrixData = await generateSettlementMatrix();
      setMatrix(matrixData);
      const owingsData = await calculateWhoOwesWhom();
      setOwings(owingsData);
    } catch (error) {
      console.error('Failed to load settlement data:', error);
      setLoadError(error ?? new Error('load failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
  }, []);

  const handleMarkAsCompleted = async (idx: number) => {
    try {
      const owing = owings[idx];
      await createSettlement({
        from_contributor_id: owing.from.id,
        from_contributor_name: owing.from.name,
        to_contributor_id: owing.to.id,
        to_contributor_name: owing.to.name,
        amount: owing.amount,
        status: 'completed',
        method: paymentMethod,
        reference: paymentRef || undefined,
      });

      setPaymentRef('');
      await refreshData();
    } catch (error) {
      console.error('Failed to mark settlement completed:', error);
      showError('Failed to record payment. Please try again.');
    }
  };

  const handleExportReport = async () => {
    try {
      const report = await generateSettlementReport(money);
      downloadText(`settlement-report-${new Date().toISOString().split('T')[0]}.txt`, report, 'text/plain');
    } catch (error) {
      console.error('Failed to export report:', error);
      showError('Failed to export report. Please try again.');
    }
  };

  const handleExportCSV = async () => {
    try {
      const csv = await exportSettlementAsCSV();
      downloadText(`settlement-data-${new Date().toISOString().split('T')[0]}.csv`, csv);
    } catch (error) {
      console.error('Failed to export CSV:', error);
      showError('Failed to export CSV. Please try again.');
    }
  };

  if (loadError) {
    return <LoadErrorBox what="settlement data" error={loadError} onRetry={refreshData} retrying={loading} />;
  }

  if (loading || !matrix) {
    return (
      <div className="wt-loading">Loading settlement data…</div>
    );
  }

  return (
    <>
      {/* Header Stats */}
      <section className="stats-grid">
        <MetricCard
          label="Total Contributors"
          value={matrix.contributors.length}
          tone="pink"
          icon="users"
        />
        <MetricCard
          label="Total Contributed"
          value={formatCurrency(Number(matrix.total_contributions))}
          tone="green"
          icon="chart"
        />
        <MetricCard
          label="Unsettled"
          value={formatCurrency(Number(matrix.unsettled_amount))}
          tone={matrix.unsettled_amount > 0 ? 'blue' : 'teal'}
          icon="alert"
        />
        <MetricCard
          label="Status"
          value={matrix.settlement_complete ? '✓ Complete' : 'Pending'}
          tone={matrix.settlement_complete ? 'green' : 'blue'}
          icon="check"
        />
      </section>

      {/* Settlements Required */}
      {owings.length === 0 ? (
        <EmptyState
          icon="check"
          title="All Settled!"
          description="Everyone has paid what they owe. The event is fully reconciled."
          action={null}
        />
      ) : (
        <>
          <div className="sr-section-header">
            <h3>Settlement Required</h3>
            <p className="sr-section-note">{owings.length} payment{owings.length !== 1 ? 's' : ''} needed to balance everyone</p>
          </div>

          <div className="sr-settlement-list">
            {owings.map((owing, idx) => (
              <div key={`${owing.from.id}-${owing.to.id}`} className="sr-settlement-item">
                <div className="sr-settlement-flow">
                  <div className="sr-from">
                    <div className="sr-person-avatar">{owing.from.name.charAt(0)}</div>
                    <div className="sr-person-info">
                      <p className="sr-name">{owing.from.name}</p>
                      <p className="sr-status owes">Owes</p>
                    </div>
                  </div>

                  <div className="sr-arrow">
                    <Icon name="arrowRight" size={20} />
                  </div>

                  <div className="sr-to">
                    <div className="sr-person-avatar">{owing.to.name.charAt(0)}</div>
                    <div className="sr-person-info">
                      <p className="sr-name">{owing.to.name}</p>
                      <p className="sr-status receives">Receives</p>
                    </div>
                  </div>
                </div>

                <div className="sr-amount">
                  <strong>{formatCurrency(Number(owing.amount))}</strong>
                </div>

                {selectedSettlement === `${owing.from.id}-${owing.to.id}` ? (
                  <div className="sr-payment-form">
                    <div className="form-group">
                      <label>Payment Method</label>
                      <SearchableSelect
                        value={paymentMethod}
                        onChange={value => setPaymentMethod(value as any)}
                        options={[
                          { value: 'bank-transfer', label: 'Bank Transfer' },
                          { value: 'cash', label: 'Cash' },
                          { value: 'card', label: 'Card' },
                          { value: 'mobile-money', label: 'Mobile Money' },
                        ]}
                      />
                    </div>
                    <div className="form-group">
                      <label>Reference / Proof (optional)</label>
                      <input
                        type="text"
                        value={paymentRef}
                        onChange={(e) => setPaymentRef(e.target.value)}
                        placeholder="e.g., Transaction ID, check number"
                      />
                    </div>
                    <div className="form-actions">
                      <button
                        className="btn-mark-done"
                        onClick={() => handleMarkAsCompleted(idx)}
                      >
                        <Icon name="check" size={14} />
                        Mark as Completed
                      </button>
                      <button
                        className="btn-cancel-form"
                        onClick={() => setSelectedSettlement(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="btn-record-payment"
                    onClick={() => setSelectedSettlement(`${owing.from.id}-${owing.to.id}`)}
                  >
                    <Icon name="plus" size={14} />
                    Record Payment
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="sr-export-section">
            <button className="btn-export" onClick={handleExportReport}>
              <Icon name="download" size={16} />
              Export Settlement Report
            </button>
            <button className="btn-export" onClick={handleExportCSV}>
              <Icon name="download" size={16} />
              Export as CSV
            </button>
          </div>
        </>
      )}

      {/* Completed Settlements */}
      {matrix.settlements.filter((s: Settlement) => s.status === 'completed').length > 0 && (
        <div className="sr-section">
          <h3>Completed Settlements</h3>
          <div className="sr-completed-list">
            {matrix.settlements
              .filter((s: Settlement) => s.status === 'completed')
              .map((settlement: Settlement) => (
                <div key={settlement.id} className="sr-completed-item">
                  <div className="sr-completed-flow">
                    <span className="sr-from-name">{settlement.from_contributor_name}</span>
                    <span className="sr-arrow">→</span>
                    <span className="sr-to-name">{settlement.to_contributor_name}</span>
                  </div>
                  <span className="sr-amount">{formatCurrency(Number(settlement.amount))}</span>
                  {settlement.reference && <span className="sr-ref"><MaskedValue value={settlement.reference} /></span>}
                </div>
              ))}
          </div>
        </div>
      )}
    </>
  );
};

export default SettlementReconciliation;
